import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// 创建 axios 实例
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器：添加 Token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 响应拦截器：处理错误
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // 网络连接失败（无响应）
    if (!error.response) {
      // 动态导入 antd message，避免循环依赖
      import('antd').then(({ message }) => {
        message.error('网络连接失败，请检查网络或服务器状态');
      });
      return Promise.reject(error);
    }

    // 401/403 跳转登录页（但不是登录接口本身）
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    if (!isLoginRequest && (error.response.status === 401 || error.response.status === 403)) {
      localStorage.removeItem('token');
      localStorage.removeItem('admin');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// 认证 API
export const authAPI = {
  login: (adminId: number) => apiClient.post('/auth/login', { adminId }),

  // Telegram Login Widget 登录
  loginWithTelegram: (telegramData: {
    id: number;
    first_name: string;
    last_name?: string;
    username?: string;
    photo_url?: string;
    auth_date: number;
    hash: string;
  }) => apiClient.post('/auth/telegram-login', telegramData),

  verify: () => apiClient.get('/auth/verify'),
};

// 管理员 API
export const adminAPI = {
  getAll: () => apiClient.get('/admins'),
  getById: (id: number) => apiClient.get(`/admins/${id}`),
  create: (data: {
    telegramId?: number | null;
    loginUsername: string;
    displayName: string;
    password: string;
    permissionLevel?: number;
  }) => apiClient.post('/admins', data),
  updatePermission: (id: number, permissionLevel: number) =>
    apiClient.put(`/admins/${id}/permission`, { permissionLevel }),
  updateStatus: (id: number, status: boolean) =>
    apiClient.put(`/admins/${id}/status`, { status }),
  delete: (id: number) => apiClient.delete(`/admins/${id}`),
  getBots: (id: number) => apiClient.get(`/admins/${id}/bots`),
  changePassword: (id: number, data: {
    oldPassword?: string;
    newPassword: string;
  }) => apiClient.put(`/admins/${id}/change-password`, data),

  // Telegram ID 验证和修改
  validateTelegramId: (telegramId: number) =>
    apiClient.post('/admins/validate-telegram-id', { telegramId }),
  updateTelegramId: (id: number, telegramId: number, skipValidation?: boolean) =>
    apiClient.put(`/admins/${id}/telegram-id`, { telegramId, skipValidation }),

  // 2FA 管理
  get2FAStatus: () => apiClient.get('/admins/my-2fa'),
  bind2FA: (password: string) => apiClient.post('/admins/bind-2fa', { password }),
  unbind2FA: (password: string, totpCode: string) =>
    apiClient.post('/admins/unbind-2fa', { password, totpCode }),
};

// 机器人 API
export const botAPI = {
  getAll: () => apiClient.get('/bots'),
  getById: (id: number) => apiClient.get(`/bots/${id}`),
  create: (data: {
    botToken: string;
    botName: string;
    description?: string;
    webhookUrl?: string;
    pollingMode?: number;
    botType?: 'accounting' | 'group_management';
  }) => apiClient.post('/bots', data),
  update: (id: number, data: {
    botName?: string;
    description?: string;
    status?: number;
    botType?: 'accounting' | 'group_management';
    joinMessage?: string | null;
  }) => apiClient.put(`/bots/${id}`, data),
  delete: (id: number) => apiClient.delete(`/bots/${id}`),
  restart: (id: number) => apiClient.post(`/bots/${id}/restart`),
  stop: (id: number) => apiClient.post(`/bots/${id}/stop`),

  // Webhook 相关 API
  getWebhookInfo: (id: number) => apiClient.get(`/bots/${id}/webhook-info`),
  setWebhook: (id: number, webhookUrl: string) =>
    apiClient.post(`/bots/${id}/set-webhook`, { webhookUrl }),
  deleteWebhook: (id: number) => apiClient.post(`/bots/${id}/delete-webhook`),

  // 管理员相关 API
  getAdmins: (id: number) => apiClient.get(`/bots/${id}/admins`),
  addAdmin: (id: number, data: { adminId: number; canManageBot?: boolean; canViewData?: boolean }) =>
    apiClient.post(`/bots/${id}/admins`, data),
  removeAdmin: (botId: number, adminId: number) =>
    apiClient.delete(`/bots/${botId}/admins/${adminId}`),

  // 模式切换 API
  switchMode: (id: number, data: { mode: 'polling' | 'webhook'; webhookUrl?: string }) =>
    apiClient.post(`/bots/${id}/switch-mode`, data),

  // 消息日志 API
  getMessages: (id: number, params?: {
    chatId?: number;
    chatType?: string;
    fromId?: number;
    messageType?: string;
    keyword?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }) => apiClient.get(`/bots/${id}/messages`, { params }),

  getChats: (id: number) => apiClient.get(`/bots/${id}/chats`),

  getMessageStatistics: (id: number) => apiClient.get(`/bots/${id}/message-statistics`),

  // Webhook 清理 API
  cleanWebhook: (id: number, dropPendingUpdates?: boolean) =>
    apiClient.post(`/bots/${id}/clean-webhook`, { dropPendingUpdates }),
  cleanInvalidWebhooks: () => apiClient.post('/bots/clean-invalid-webhooks'),
};

// 群组管理 API
export const groupAPI = {
  getAll: (botId?: number) => apiClient.get('/groups', { params: { botId } }),
  getById: (id: number) => apiClient.get(`/groups/${id}`),
  add: (data: { botId: number; groupId: number; description?: string }) =>
    apiClient.post('/groups/add', data),
  update: (id: number, data: { description?: string; status?: number }) =>
    apiClient.put(`/groups/${id}`, data),
  delete: (id: number, mode?: 'soft' | 'hard') =>
    apiClient.delete(`/groups/${id}${mode ? `?mode=${mode}` : ''}`),
  refresh: (id: number) => apiClient.post(`/groups/${id}/refresh`),
  generateInviteLink: (
    id: number,
    data?: { expireDate?: number; memberLimit?: number }
  ) => apiClient.post(`/groups/${id}/invite-link`, data),
  leave: (id: number) => apiClient.post(`/groups/${id}/leave`),
  getStatistics: (botId?: number) =>
    apiClient.get('/groups/statistics/summary', { params: { botId } }),
};

// 自动回复 API（群组）
export const autoReplyAPI = {
  getAll: (params?: { botId?: number | null; groupId?: number | null; status?: number }) =>
    apiClient.get('/auto-replies', { params }),
  getById: (id: number) => apiClient.get(`/auto-replies/${id}`),
  create: (data: {
    botId?: number | null;
    groupId?: number | null;
    matchType: string;
    keyword: string;
    replyText: string;
    replyType?: string;
    mediaUrl?: string;
    parseMode?: string;
    deleteTrigger?: number;
    replyProbability?: number;
    permissionLevel?: number;
    inlineButtons?: any[] | null;
    priority?: number;
    status?: number;
    description?: string;
  }) => apiClient.post('/auto-replies', data),
  update: (id: number, data: any) => apiClient.put(`/auto-replies/${id}`, data),
  delete: (id: number) => apiClient.delete(`/auto-replies/${id}`),
  batchUpdateStatus: (ids: number[], status: number) =>
    apiClient.put('/auto-replies/batch/status', { ids, status }),
  batchDelete: (ids: number[]) => apiClient.delete('/auto-replies/batch', { data: { ids } }),
  testDynamicVar: (data: {
    api_url: string;
    method: 'GET' | 'POST';
    custom_headers?: Array<{ name: string; value_from_env: string }>;
    request_body_params?: Array<{ key: string; param: string }>;
    query_params?: Array<{ key: string; param: string }>;
    test_context?: {
      telegram_user_id?: number;
      telegram_username?: string;
      telegram_first_name?: string;
      telegram_last_name?: string;
      bot_id?: number;
      bot_username?: string;
      bot_name?: string;
      bot_invite_link?: string;
      telegram_group_id?: number;
      telegram_group_title?: string;
      telegram_group_invite_link?: string;
    };
  }) => apiClient.post('/auto-replies/test-dynamic-var', data),
};

// 回复模板 API（私聊）
export const replyTemplateAPI = {
  getAll: (params?: { botId?: number | null; triggerType?: string; status?: number }) =>
    apiClient.get('/reply-templates', { params }),
  getById: (id: number) => apiClient.get(`/reply-templates/${id}`),
  create: (data: {
    botId?: number | null;
    triggerType: string;
    triggerValue: string;
    replyText: string;
    replyType?: string;
    mediaUrl?: string;
    parseMode?: string;
    showKeyboard?: number;
    inlineButtons?: any;
    permissionLevel?: number;
    priority?: number;
    status?: number;
    description?: string;
  }) => apiClient.post('/reply-templates', data),
  update: (id: number, data: any) => apiClient.put(`/reply-templates/${id}`, data),
  delete: (id: number) => apiClient.delete(`/reply-templates/${id}`),
};

// 键盘按钮 API
export const keyboardAPI = {
  getAll: (params?: { botId?: number | null; status?: number }) =>
    apiClient.get('/keyboard-buttons', { params }),
  getLayout: (botId: number | null) =>
    apiClient.get(`/keyboard-buttons/layout/${botId === null ? 'null' : botId}`),
  getById: (id: number) => apiClient.get(`/keyboard-buttons/${id}`),
  create: (data: {
    botId?: number | null;
    buttonText: string;
    buttonType?: string;
    buttonValue?: string;
    rowIndex?: number;
    colIndex?: number;
    status?: number;
    description?: string;
  }) => apiClient.post('/keyboard-buttons', data),
  update: (id: number, data: any) => apiClient.put(`/keyboard-buttons/${id}`, data),
  delete: (id: number) => apiClient.delete(`/keyboard-buttons/${id}`),
  replaceAll: (botId: number | null, buttons: any[]) =>
    apiClient.post('/keyboard-buttons/replace-all', { botId, buttons }),
};

// 命令式回复 API（只读）
export const commandAPI = {
  getAll: () => apiClient.get('/commands'),
  getByCategory: () => apiClient.get('/commands/by-category'),
  getByPermission: () => apiClient.get('/commands/by-permission'),
  getById: (id: string) => apiClient.get(`/commands/${id}`),
};

// 命令响应模板 API（可编辑）
export const commandTemplateAPI = {
  getAll: (params?: { category?: string; status?: number }) =>
    apiClient.get('/command-templates', { params }),
  getGrouped: () => apiClient.get('/command-templates/grouped'),
  getById: (id: number) => apiClient.get(`/command-templates/${id}`),
  create: (data: {
    template_key: string;
    template_name: string;
    template_text: string;
    category?: string;
    variables?: string;
    parse_mode?: string;
    description?: string;
    status?: number;
  }) => apiClient.post('/command-templates', data),
  update: (id: number, data: any) => apiClient.put(`/command-templates/${id}`, data),
  delete: (id: number) => apiClient.delete(`/command-templates/${id}`),
  clearCache: () => apiClient.post('/command-templates/clear-cache'),
};

// 消息日志 API
export const messageLogAPI = {
  // 获取群组消息记录
  getByChat: (chatId: string | number, params?: {
    page?: number;
    limit?: number;
    messageType?: string;
    fromUsername?: string;
  }) => apiClient.get(`/message-logs/${chatId}`, { params }),

  // 获取群组消息统计
  getStats: (chatId: string | number) => apiClient.get(`/message-logs/${chatId}/stats`),
};

// 群组管理设置 API（群管类型机器人专用）
export const botGroupSettingsAPI = {
  getAll: (botId?: number) =>
    apiClient.get('/bot-group-settings/all', { params: botId ? { botId } : {} }),
  get: (botId: number, groupId: number) =>
    apiClient.get('/bot-group-settings', { params: { botId, groupId } }),
  save: (data: {
    botId: number;
    groupId: number;
    sensitiveWords?: string[];
    sensitiveAction?: 'delete' | 'mute' | 'kick';
    muteDuration?: number;
    transferContact?: string;
    transferKeywords?: string[];
    welcomeMessage?: string;
    welcomeEnabled?: boolean;
    deleteJoinMessages?: boolean;
    groupRules?: string;
  }) => apiClient.post('/bot-group-settings', data),
  delete: (botId: number, groupId: number) =>
    apiClient.delete('/bot-group-settings', { params: { botId, groupId } }),
};

// 频道管理 API
export const channelAPI = {
  getAll: (botId?: number) => apiClient.get('/channels', { params: { botId } }),
  getById: (id: number) => apiClient.get(`/channels/${id}`),
  create: (data: {
    botId: number;
    channelId: number | string;
    channelUsername?: string;
    title?: string;
    inviteLink?: string;
  }) => apiClient.post('/channels', data),
  update: (id: number, data: { title?: string; status?: number }) =>
    apiClient.put(`/channels/${id}`, data),
  refresh: (id: number) => apiClient.post(`/channels/${id}/refresh`),
  delete: (id: number) => apiClient.delete(`/channels/${id}`),
};

// 广播任务 API
export const broadcastAPI = {
  getAll: (params?: { botId?: number; status?: number }) =>
    apiClient.get('/broadcast-tasks', { params }),
  getById: (id: number) => apiClient.get(`/broadcast-tasks/${id}`),
  create: (data: {
    botId: number;
    targetId?: number | string | null;
    targetType?: 'group' | 'channel' | 'private' | 'all_private';
    title?: string;
    content: string;
    parseMode?: string;
    mediaType?: string | null;
    mediaUrl?: string | null;
    inlineButtons?: any[] | null;
    scheduleType: 'once' | 'interval' | 'cron';
    scheduleAt?: string | null;
    intervalMinutes?: number | null;
    cronExpr?: string | null;
    pinMessage?: boolean;
    pinDuration?: number | null;
    silent?: boolean;
  }) => apiClient.post('/broadcast-tasks', data),
  update: (id: number, data: any) => apiClient.put(`/broadcast-tasks/${id}`, data),
  execute: (id: number) => apiClient.post(`/broadcast-tasks/${id}/execute`),
  pause: (id: number) => apiClient.post(`/broadcast-tasks/${id}/pause`),
  resume: (id: number) => apiClient.post(`/broadcast-tasks/${id}/resume`),
  delete: (id: number) => apiClient.delete(`/broadcast-tasks/${id}`),
  getPrivateUsersCount: (botId: number) =>
    apiClient.get('/broadcast-tasks/private-users-count', { params: { botId } }),
  getSendResults: (taskId: number, params?: { batchNo?: number; page?: number; pageSize?: number }) =>
    apiClient.get(`/broadcast-tasks/${taskId}/send-results`, { params }),
  getSendBatches: (taskId: number) =>
    apiClient.get(`/broadcast-tasks/${taskId}/send-batches`),
};

// 敏感词规则 API
export const sensitiveWordAPI = {
  getAll: (filters?: { botId?: number | null; action?: string; status?: number }) =>
    apiClient.get('/sensitive-words', {
      params: {
        botId: filters?.botId === null ? 'null' : filters?.botId,
        action: filters?.action,
        status: filters?.status,
      },
    }),
  getById: (id: number) => apiClient.get(`/sensitive-words/${id}`),
  create: (data: {
    botId?: number | null;
    groupId?: number | null;
    word: string;
    matchType?: 'contains' | 'exact' | 'regex';
    action?: 'delete' | 'mute' | 'kick';
    muteDuration?: number;
    whitelistEnabled?: boolean;
    priority?: number;
    status?: number;
    description?: string;
  }) => apiClient.post('/sensitive-words', data),
  update: (id: number, data: any) => apiClient.put(`/sensitive-words/${id}`, data),
  delete: (id: number) => apiClient.delete(`/sensitive-words/${id}`),
  batchUpdateStatus: (ids: number[], status: number) =>
    apiClient.put('/sensitive-words/batch/status', { ids, status }),
  batchDelete: (ids: number[]) =>
    apiClient.delete('/sensitive-words/batch', { data: { ids } }),
};

// 命令开关设置 API
export const commandSettingAPI = {
  getAll: (botId: number) => apiClient.get(`/command-settings/${botId}`),
  setEnabled: (botId: number, commandId: string, enabled: boolean) =>
    apiClient.put(`/command-settings/${botId}/${commandId}`, { enabled }),
  clearCache: (botId: number) => apiClient.post(`/command-settings/${botId}/clear-cache`),
};

// 文件上传 API
// 通知模板 API
export const notificationTemplateAPI = {
  getAll: () => apiClient.get('/notification-templates'),
  getById: (id: number) => apiClient.get(`/notification-templates/${id}`),
  create: (data: {
    template_key: string;
    template_name: string;
    content: string;
    parse_mode?: string;
    media_type?: string | null;
    media_url?: string | null;
    inline_buttons?: any[] | null;
    params_schema?: Array<{ name: string; required: boolean; default?: string; description?: string }> | null;
    status?: number;
    bot_ids: number[];
  }) => apiClient.post('/notification-templates', data),
  update: (id: number, data: any) => apiClient.put(`/notification-templates/${id}`, data),
  delete: (id: number) => apiClient.delete(`/notification-templates/${id}`),
  getLogs: (params?: {
    bot_id?: number;
    template_key?: string;
    user_id?: number;
    status?: string;
    page?: number;
    page_size?: number;
  }) => apiClient.get('/notification-templates/logs/list', { params }),
};

// 自定义动态 Emoji API
export const customEmojiAPI = {
  getAll: (params?: { category?: string; status?: number; botId?: number }) =>
    apiClient.get('/custom-emojis', { params }),
  getCategories: () => apiClient.get('/custom-emojis/categories'),
  getById: (id: number) => apiClient.get(`/custom-emojis/${id}`),
  create: (data: {
    customEmojiId: string;
    emojiFallback?: string;
    name?: string;
    category?: string;
    botId?: number | null;
  }) => apiClient.post('/custom-emojis', data),
  update: (id: number, data: {
    name?: string;
    category?: string;
    emojiFallback?: string;
    status?: number;
  }) => apiClient.put(`/custom-emojis/${id}`, data),
  delete: (id: number) => apiClient.delete(`/custom-emojis/${id}`),
  refreshThumbnails: () => apiClient.post('/custom-emojis/refresh-thumbnails'),
};

const API_ROOT_URL = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:3001';

export const uploadAPI = {
  upload: (file: File, onProgress?: (percent: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<{
      url: string;
      filename: string;
      originalName: string;
      size: number;
      mimetype: string;
      type: 'photo' | 'video';
    }>('/uploads', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    });
  },
  getAll: () => apiClient.get<Array<{
    filename: string;
    url: string;
    size: number;
    type: 'photo' | 'video';
    createdAt: string;
  }>>('/uploads'),
  delete: (filename: string) => apiClient.delete(`/uploads/${filename}`),
  getSettings: () => apiClient.get<{ uploadBaseUrl: string }>('/uploads/settings'),
  saveSettings: (uploadBaseUrl: string) =>
    apiClient.put('/uploads/settings', { uploadBaseUrl }),
  getFullUrl: (urlPath: string) => {
    if (!urlPath) return '';
    if (urlPath.startsWith('http://') || urlPath.startsWith('https://')) return urlPath;
    return `${API_ROOT_URL}${urlPath}`;
  },
};

export default apiClient;
